import { useState, useEffect } from 'react';

import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import NodeControlForm from '../NodeControlForm';

import type { NodeControlsProps } from '../../dataProvider/types';

type AiModel = 'gpt-realtime' | 'gpt-realtime-mini';
type AiVoice = 'alloy' | 'ash' | 'ballad' | 'coral' | 'echo' | 'sage' | 'shimmer' | 'verse' | 'marin' | 'cedar';
type TranscriptionModel = 'whisper-1' | 'gpt-4o-mini-transcribe' | 'gpt-4o-transcribe' | 'gpt-4o-transcribe-diarize';

export interface AiAssistantData extends Record<string, unknown> {
  instructions?: string;
  model?: AiModel;
  voice?: AiVoice;
  transcription?: {
    enabled: boolean;
    model?: TranscriptionModel;
  };
}

interface AiAssistantControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: AiAssistantData;
}

export default function AiAssistantControls({ updateNodeData, data }: AiAssistantControlsProps): React.ReactElement {
  const translate = useTranslate();
  const defaultInstructions = translate('callflows.nodes.ai_assistant.defaultInstructions');

  const [instructions, setInstructions] = useState<string>(data.instructions ?? defaultInstructions);
  const [model, setModel] = useState<AiModel>(data.model ?? 'gpt-realtime');
  const [voice, setVoice] = useState<AiVoice>(data.voice ?? 'marin');
  const [transcriptionEnabled, setTranscriptionEnabled] = useState<boolean>(data.transcription?.enabled ?? false);
  const [transcriptionModel, setTranscriptionModel] = useState<TranscriptionModel>(
    data.transcription?.model ?? 'whisper-1'
  );

  const onAiAssistantDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    const transcription = transcriptionEnabled ? { enabled: true, model: transcriptionModel } : undefined;
    flowchartLogger.debug('AiAssistantControls: onAiAssistantDataUpdate', {
      instructions,
      model,
      voice,
      transcription,
    });
    if (instructions === undefined || instructions === '') {
      flowchartLogger.debug('AiAssistantControls: instructions is undefined or empty. Update cancelled.');
      return;
    }
    updateNodeData({ instructions, model, voice, transcription });
  };

  useEffect(() => {
    const transcription = transcriptionEnabled ? { enabled: true, model: transcriptionModel } : undefined;
    flowchartLogger.debug('AiAssistantControls: props changed', { instructions, model, voice, transcription });
    updateNodeData({ instructions, model, voice, transcription });
  }, [instructions, model, voice, transcriptionEnabled, transcriptionModel, updateNodeData]);

  return (
    <NodeControlForm
      onSubmit={onAiAssistantDataUpdate}
      isDisabled={instructions === ''}
      data={data}
      updateNodeData={updateNodeData}
    >
      <TextField
        value={instructions}
        onChange={(e) => {
          setInstructions(e.target.value);
        }}
        label={translate('callflows.nodes.ai_assistant.instructions')}
        multiline
        rows={8}
        required
      />
      <FormControl margin='none' fullWidth sx={{ mb: 1 }} size='small' variant='outlined'>
        <InputLabel id='ai-assistant-model-label'>{translate('callflows.nodes.ai_assistant.model')}</InputLabel>
        <Select
          required
          labelId='ai-assistant-model-label'
          value={model}
          label={translate('callflows.nodes.ai_assistant.model')}
          onChange={(event) => {
            setModel(event.target.value);
          }}
        >
          <MenuItem value='gpt-realtime'>gpt-realtime</MenuItem>
          <MenuItem value='gpt-realtime-mini'>gpt-realtime-mini</MenuItem>
        </Select>
      </FormControl>
      <FormControl margin='none' fullWidth sx={{ mb: 1 }} size='small' variant='outlined'>
        <InputLabel id='ai-assistant-voice-label'>{translate('callflows.nodes.ai_assistant.voice')}</InputLabel>
        <Select
          required
          labelId='ai-assistant-voice-label'
          value={voice}
          label={translate('callflows.nodes.ai_assistant.voice')}
          onChange={(event) => {
            setVoice(event.target.value);
          }}
        >
          <MenuItem value='alloy'>alloy</MenuItem>
          <MenuItem value='ash'>ash</MenuItem>
          <MenuItem value='ballad'>ballad</MenuItem>
          <MenuItem value='coral'>coral</MenuItem>
          <MenuItem value='echo'>echo</MenuItem>
          <MenuItem value='sage'>sage</MenuItem>
          <MenuItem value='shimmer'>shimmer</MenuItem>
          <MenuItem value='verse'>verse</MenuItem>
          <MenuItem value='marin'>marin</MenuItem>
          <MenuItem value='cedar'>cedar</MenuItem>
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={transcriptionEnabled}
            onChange={(event) => {
              setTranscriptionEnabled(event.target.checked);
            }}
          />
        }
        label={translate('callflows.nodes.ai_assistant.transcription.enabled')}
        sx={{ mb: 1 }}
      />
      {transcriptionEnabled && (
        <FormControl margin='none' fullWidth sx={{ mb: 1 }} size='small' variant='outlined'>
          <InputLabel id='ai-assistant-transcription-model-label'>
            {translate('callflows.nodes.ai_assistant.transcription.model')}
          </InputLabel>
          <Select
            labelId='ai-assistant-transcription-model-label'
            value={transcriptionModel}
            label={translate('callflows.nodes.ai_assistant.transcription.model')}
            onChange={(event) => {
              setTranscriptionModel(event.target.value);
            }}
          >
            <MenuItem value='whisper-1'>whisper-1</MenuItem>
            <MenuItem value='gpt-4o-mini-transcribe'>gpt-4o-mini-transcribe</MenuItem>
            <MenuItem value='gpt-4o-transcribe'>gpt-4o-transcribe</MenuItem>
            <MenuItem value='gpt-4o-transcribe-diarize'>gpt-4o-transcribe-diarize</MenuItem>
          </Select>
        </FormControl>
      )}
    </NodeControlForm>
  );
}
